import styles from "./Home.module.css";
import Teams from "../components/Teams";
import { useEffect, useRef, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import {
  selectTeams,
} from '../redux/slices/teamsSlice';
import { selectUser } from '../redux/slices/userSlice';
import {
  fetchAssistants,
  selectAssistants,
  clearAssistantsError,
  setAssistants,
  deleteAssistant,
  clearDeleteResult,
  fetchUpdateForm,
  fetchAssistantDefault,
  clearUpdateResult,
  fetchFileOptions,
} from '../redux/slices/assistantsSlice';
import { useToaster } from '../components/Toaster';
import { useNavigate } from 'react-router-dom';
import CheckModal from '../components/CheckModal';
import AssistantUpdateModal from '../components/AssistantUpdateModal';
import ShareModal from '../components/ShareModal';

export default function Home() {
  const teams = useSelector(selectTeams);
  const user = useSelector(selectUser);
  const assistants = useSelector(selectAssistants);
  const [checkModalOpen, setCheckModalOpen] = useState(false);
  const [assistantNameDelete, setAssistantNameDelete] = useState('');
  const [assistantTokenDelete, setAssistantTokenDelete] = useState('');
  const [assistantUpdateModalOpen, setAssistantUpdateModalOpen] = useState(false);
  const [shareModalOpen, setShareModalOpen] = useState(false);
  const [shareAssistantToken, setShareAssistantToken] = useState('');
  const [shareAssistantName, setShareAssistantName] = useState('');

  const isDisabled =
    teams.teams.length &&
    ![1, 2].includes(
      teams.teams.find((item) => item.token === teams.team_selected)?.account_role
    );

  const hasMounted = useRef(false);
  const dispatch = useDispatch();
  const { push } = useToaster();
  const navigate = useNavigate();

  const POLL_MS = 10_000;
  const pollTimeoutRef = useRef(null);
  const pollIntervalRef = useRef(null);
  const pollInFlightRef = useRef(false);
  const assistantsListRef = useRef([]);

  useEffect(() => {
    assistantsListRef.current = assistants?.assistants ?? [];
  }, [assistants?.assistants]);

  const stopPolling = () => {
    if (pollTimeoutRef.current) {
      clearTimeout(pollTimeoutRef.current);
      pollTimeoutRef.current = null;
    }
    if (pollIntervalRef.current) {
      clearInterval(pollIntervalRef.current);
      pollIntervalRef.current = null;
    }
    pollInFlightRef.current = false;
  };

  const mergeByTokenKeepOrder = (prevList, patchList) => {
    const patchMap = new Map((patchList || []).map((x) => [x.assistant_token, x]));

    const merged = (prevList || []).map((oldItem) => {
      const patch = patchMap.get(oldItem.assistant_token);
      return patch ? { ...oldItem, ...patch } : oldItem;
    });

    const prevSet = new Set((prevList || []).map((x) => x.assistant_token));

    for (const p of (patchList || [])) {
      if (p?.assistant_token && !prevSet.has(p.assistant_token)) merged.push(p);
    }

    return merged;
  };

  const pollAssistantsBackgroundOnce = async () => {
    if (pollInFlightRef.current) return;

    const account_token = user?.data?.token;
    const team_token = teams?.team_selected;

    if (!account_token || !team_token) {
      stopPolling();
      return;
    }

    const currentList = assistantsListRef.current || [];

    const buildingTokens = currentList
      .filter((a) => a && a.state === 0 && a.assistant_token)
      .map((a) => a.assistant_token);

    if (buildingTokens.length === 0) {
      stopPolling();
      return;
    }

    pollInFlightRef.current = true;

    try {
      const res = await fetch('/api/get_assistants_background/', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          account_token,
          team_token,
          assistant_tokens: buildingTokens,
        }),
      });

      const json = await res.json().catch(() => ({}));

      if (!res.ok || json?.status !== 'OK') {
        stopPolling();
        return;
      }

      const patchList = Array.isArray(json?.data) ? json.data : [];
      const merged = mergeByTokenKeepOrder(currentList, patchList);

      dispatch(setAssistants(merged));
      assistantsListRef.current = merged;

      const stillBuilding = merged.some((a) => a && a.state === 0);

      if (!stillBuilding) stopPolling();
    } catch (e) {
      stopPolling();
    } finally {
      pollInFlightRef.current = false;
    }
  };

  const startPollingIfNeeded = () => {
    if (pollTimeoutRef.current || pollIntervalRef.current) return;

    pollTimeoutRef.current = setTimeout(() => {
      pollTimeoutRef.current = null;

      pollAssistantsBackgroundOnce();

      pollIntervalRef.current = setInterval(() => {
        pollAssistantsBackgroundOnce();
      }, POLL_MS);
    }, POLL_MS);
  };

  useEffect(() => {
    const list = assistants?.assistants ?? [];
    const hasBuilding = list.some((a) => a && a.state === 0);

    if (!hasBuilding) {
      stopPolling();
      return;
    }

    startPollingIfNeeded();

    return () => {
    };
  }, [assistants?.assistants, teams?.team_selected, user?.data?.token]);

  useEffect(() => {
    return () => stopPolling();
  }, []);

  useEffect(() => {
    if (!hasMounted.current) {
      hasMounted.current = true;
      return;
    }

    if ((teams?.no_teams == false) && ((teams?.team_selected ?? '') != '') && ((user?.data?.token ?? '') != '')) {
      dispatch(fetchAssistants({
        team_token: teams.team_selected,
        account_token: user.data.token,
      }));
    }
  }, [teams?.no_teams, teams?.team_selected]);

  useEffect(() => {
    const e = assistants.error;
    const msg = (e && (e.msg || e.message)) || '';

    if (msg) {
      push({ title: '發生錯誤', message: msg, variant: 'danger' });
      dispatch(clearAssistantsError());
    }
  }, [assistants.error, push, dispatch]);

  useEffect(() => {
    const r = assistants.deleteResult;
    const ok = r.hasOwnProperty("token");

    if (!ok) return;

    push({
      title: "刪除完成",
      message: `已成功刪除 「${assistantNameDelete}」`,
      variant: "success",
    });

    dispatch(clearDeleteResult());
    setCheckModalOpen(false);

    if ((teams?.team_selected ?? "") !== "" && (user?.data?.token ?? "") !== "") {
      dispatch(fetchAssistants({ team_token: teams.team_selected, account_token: user.data.token }));
    }
  }, [assistants.deleteResult, push, dispatch]);

  useEffect(() => {
    const r = assistants.updateResult;
    const ok = r.hasOwnProperty("token");

    if (!ok) return;

    push({
      title: "更新成功",
      message: `代理人更新成功！`,
      variant: "success",
    });

    dispatch(clearUpdateResult());
    setAssistantUpdateModalOpen(false);

    if ((teams?.team_selected ?? "") !== "" && (user?.data?.token ?? "") !== "") {
      dispatch(fetchAssistants({ team_token: teams.team_selected, account_token: user.data.token }));
    }
  }, [assistants.updateResult, push, dispatch]);

  const handleDeleteClick = (item) => {
    setAssistantTokenDelete(item?.assistant_token);
    setAssistantNameDelete(item?.name);
    setCheckModalOpen(true);
  };

  const handleDelete = (assistant_token) => {
    const team_token = teams.team_selected;
    const account_token = user.data.token;

    dispatch(deleteAssistant({
      team_token,
      account_token,
      assistant_token,
    }));
  };

  const handleShareClick = (item) => {
    setShareAssistantToken(item?.assistant_token || '');
    setShareAssistantName(item?.name || '（未命名）');
    setShareModalOpen(true);
  };

  return (
    <>
      <section className={styles.teamsWrap}>
        <div className={`${styles.panel} mt-2`}>
          <div className={`px-2 px-md-5 py-2 py-md-5 ${styles.content} row g-4`}>
            <div className="col-12">
              <div className={`p-md-4 ${styles.block}`}>
                <h4 className={styles.sectionTitle}>
                  <i className="bi bi-diagram-3 me-2" />
                  團隊選擇
                </h4>
                <div className="mt-2">
                  <Teams />
                </div>
              </div>
            </div>

            <div className="col-12 mt-2 mt-md-4">
              <div className={`p-md-4 ${styles.block}`}>
                <h4 className={`${styles.sectionTitle}`}>
                  <i className="bi-robot me-2" />
                  AI 代理人
                </h4>

                <div className="row">
                  {((assistants?.assistants ?? []).length == 0) && (
                    <div className="col-12 mt-2 d-flex justify-content-center align-items-center">
                      <span className={`${styles.labelDot} me-2`} aria-hidden="true" />暫無代理人，請點擊下方按鈕建立代理人！
                    </div>
                  )}

                  {(assistants?.assistants ?? []).map((item) => {
                    const isBuilding = item.state == 0;
                    const desc = (item.description || '').trim();

                    return (
                      <div className={`col-12 col-md-6 ${styles.asCol}`} key={item.assistant_token}>
                        <div className={`card ${styles.asCard} ${isBuilding ? styles.asCardBuilding : ''}`}>
                          <div className={`card-body ${styles.asBody}`}>
                            <div className={styles.asTop}>
                              <h5 className={`card-title ${styles.asTitle}`} title={item.name || ''}>
                                {item.name || '（未命名）'}
                              </h5>

                              <span
                                className={`${styles.asStatus} ${
                                  isBuilding ? styles.asStatusBuilding : styles.asStatusReady
                                }`}
                              >
                                {isBuilding ? (
                                  <>
                                    <span className={styles.asDot} aria-hidden="true" />
                                    更新中
                                  </>
                                ) : (
                                  <>
                                    <i className="bi bi-check2-circle" aria-hidden="true" />
                                    可用
                                  </>
                                )}
                              </span>
                            </div>

                            <p className={styles.asDesc} title={desc}>
                              {desc || '（沒有描述）'}
                            </p>

                            <div className={styles.asBottom}>
                              <div className={styles.asMeta}>
                                <i className="bi bi-clock me-1" aria-hidden="true" />
                                更新：{item.update_time || '—'}
                              </div>

                              <div className={styles.asActions}>
                                {isBuilding ? (
                                  <div className={styles.asBuildingLine}>
                                    <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true" />
                                    <span>更新中</span>
                                  </div>
                                ) : (
                                  <>
                                    <button
                                      type="button"
                                      className={`${styles.asBtn} ${styles.asBtnChat}`}
                                      disabled={isBuilding}
                                      onClick={() => window.open(`/chat/${item.assistant_token}`, "_blank", "noopener,noreferrer")}
                                    >
                                      對話
                                    </button>

                                    <button
                                      type="button"
                                      className={`${styles.asBtn} ${styles.asBtnShare}`}
                                      disabled={isBuilding}
                                      onClick={() => handleShareClick(item)}
                                    >
                                      分享
                                    </button>

                                    <button
                                      type="button"
                                      className={styles.asBtn}
                                      disabled={isDisabled}
                                      onClick={() => {
                                        dispatch(fetchUpdateForm({
                                          team_token: teams.team_selected,
                                          account_token: user.data.token,
                                          assistant_token: item.assistant_token,
                                        }));

                                        dispatch(fetchAssistantDefault());

                                        dispatch(fetchFileOptions({
                                          team_token: teams.team_selected,
                                          account_token: user.data.token,
                                        }));

                                        setAssistantUpdateModalOpen(true);
                                      }}
                                    >
                                      編輯
                                    </button>
                                    <button
                                      type="button"
                                      className={`${styles.asBtn} ${styles.asBtnDanger}`}
                                      disabled={isDisabled}
                                      onClick={() => handleDeleteClick(item)}
                                    >
                                      刪除
                                    </button>
                                  </>
                                )}
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    );
                  })}

                  <div className="col-12 mt-4 d-flex justify-content-center">
                    <button
                      type="button"
                      className={styles.btnSave}
                      disabled={isDisabled}
                      onClick={() => { navigate('/assistant-create', { replace: true }); }}
                    >
                      <i className="bi-magic me-1" />
                      建立代理人
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
      <CheckModal
        modalOpen={checkModalOpen}
        title={'請確認'}
        msg={`您是否要刪除「${assistantNameDelete}」？`}
        afterYes={() => handleDelete(assistantTokenDelete)}
        afterNo={() => setCheckModalOpen(false)}
      />
      <AssistantUpdateModal
        modalOpen={assistantUpdateModalOpen}
        onHide={() => setAssistantUpdateModalOpen(false)}
      />

      <ShareModal
        modalOpen={shareModalOpen}
        assistantName={shareAssistantName}
        assistantToken={shareAssistantToken}
        onHide={() => setShareModalOpen(false)}
        onCopySuccess={() => {
          push({
            title: '已複製',
            message: '對話網址已複製到剪貼簿',
            variant: 'success',
          });
        }}
        onCopyFail={() => {
          push({
            title: '複製失敗',
            message: '無法自動複製，請手動選取網址後複製',
            variant: 'danger',
          });
        }}
      />
    </>
  );
}
